import { inject, Injectable, Injector, OnDestroy, signal } from '@angular/core';
import { Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { CreateUser } from '@interfaces/create-user.i';
import { catchError, defer, filter, firstValueFrom, interval, map, mergeMap, Observable, of, retryWhen, shareReplay, Subject, switchMap, take, takeUntil, throwError, timeout, timer } from 'rxjs';
import { CommonVariable } from 'src/app/app.constants';
import { v4 as uuidv4 } from "uuid";
import { CommonService } from '../../common/common.service';
import { FirestoreDataService } from "../../firestore-data.service";
import { SalesJourneyIdentityService } from "../../sales-journey-identity.service";

@Injectable({
  providedIn: 'root'
})
export class RequestUserService implements OnDestroy {
  private commonService = inject(CommonService);
  private salesJourneyIdentityService = inject(SalesJourneyIdentityService);
  private destroy$ = new Subject<void>();
  preferredCurrency: string;
  public checkApiPostReqProfileImg;
  public checkApiPostReqRemovePhoto;
  public socialPassword = signal("")
  constructor(private injector: Injector, private firestoreDataService: FirestoreDataService) {
    this.checkApiPostReqProfileImg = false;
    this.checkApiPostReqRemovePhoto = false;
    this.preferredCurrency = "GBP";
    this.getSocialPasswordSecretKey();
  }
  // This function is used to get the request body for to validate request is validate or not
  private validateRequest(): boolean {
    return true;
  }

  /**
   * This function is used to get the user balance details.
   * @return {Object} - Returns user balance details  [this function is not call anywhere so comment it ]
   */
  public getUserBalanceDetails() {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          return {
            request: [
              {
                session_ID: "CommonService.session.session_id",
                action: "get_account_details",
                website: "Lotto Social",
                website_id: "33",
                source_site: "mobi.lottosocial.com",
                page_id: "7",
                screen_id: "7.1",
                module_name: "get_balance_details",
                customer_id: userId,
              },
            ],
          };
        }));
    }
  }

  checkSocialSignIn(user: CreateUser, anonymousId: string): Data {
    if ((!user?.password || user?.password === "hatchster@Social2024") && user?.social_id && user?.connect_type) {
      return {
        mobile: anonymousId,
        password: this.socialPassword(),
        spoof_msn: true,
        social_id: user?.social_id,
        connect_type: user?.connect_type,
      };
    }
    return {};
  }

  // This function checks if the user is allowed to sign up
  public signupRequestForSales(user: CreateUser, anonymousId) {
    if (this.validateRequest()) {
      const deviceDetails = {
        IP: CommonVariable?.userIP,
        browser: navigator?.userAgent,
        device_type: CommonVariable?.device?.model,
      };
      const module = {
            module_name: "partial_registration_new_user_v2",
            anonymous_id: anonymousId,
            website_id: "33",
            session_id: "123456789987654321123456789987456789",
            mobile: user?.phoneNumber ? user?.phoneNumber?.split("+")[1]?.replace(/[^\d]/g, '') : null,
            password: user?.password,
            country_code: user?.country_code ? user.country_code.replace(/[^\d]/g, '') : "44",
            email: user?.email,
            ...(user?.date_of_birth ? { date_of_birth: user.date_of_birth } : {}),
            voucher_code: "",
            prosub_id: localStorage.getItem("prosub_id") ? localStorage.getItem("prosub_id") : user?.prosub_id,
            tp1: user?.tp1,
            tp2: user?.tp2,
            tp3: user?.tp3,
            t_id: user?.t_id,
            other: user?.other,
            device_details: deviceDetails,
            language_code: CommonVariable.preferredLanguage,
            preferred_currency: user?.preferred_currency ?? CommonVariable.preferredCurrency,
            msn_validated: true,
            msn_validation_source: "validatemsnv2",
            ...this.checkSocialSignIn(user, anonymousId),
            calling_method: "db"
          };
      return {
        modules: [
          this.salesJourneyIdentityService.applyIdentityEnvelope(module, {
            anonymousId,
            msn: user?.phoneNumber,
          }),
        ],
      };
    }
  }
  public signupRequestInstant(user: CreateUser, anonymousId: string) {
    if (this.validateRequest()) {
      const module = {
            module_name: "partial_registration_new_user_instant_v2",
            anonymous_id: anonymousId,
            mobile: user?.phoneNumber ? user?.phoneNumber?.split("+")[1] : null,
            password: user?.password,
            preferred_currency: user?.preferred_currency ? user?.preferred_currency : "GBP",
            ...this.checkSocialSignIn(user, anonymousId),
            calling_method: "db"
          };
      return {
        modules: [
          this.salesJourneyIdentityService.applyIdentityEnvelope(module, {
            anonymousId,
            msn: user?.phoneNumber,
          }),
        ],
      };
    }
  }

  // This function is used to get the request body for update customer password  [this function is not call anywhere so comment it ]
  getUserPasswordRequestChange(passwordDetails) {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          return {
            request: [
              {
                session_ID: "CommonService.session.session_id",
                page_ID: "7",
                screen_id: "7.5",
                action: "profile_pwd_update",
                website: "Lotto Social",
                website_id: "33",
                source_site: "mobi.lottosocial.com",
                module_name: "update_customer_password",
                customer_id: userId,
                current_password: passwordDetails.currentPassword,
                new_password: passwordDetails.newPassword,
                confirm_password: passwordDetails.repeatNewPassword,
              },
            ],
          };
        }));
    }
  }

  // This function is used to get the request body for forgot password request  [this function is not call anywhere so comment it ]
  getPasswordRecoveryRequest(countryCode, msn) {
    return {
      request: [
        {
          session_ID: "123456789987654321123456789987456321",
          page_ID: "1",
          screen_id: "1.1",
          action: "forgetpwd_mobile_app",
          website: "Lotto Social",
          website_id: "33",
          source_site: "mobi.lottosocial.com",
          module_name: "forget_password",
          mobile: msn,
          country_code: countryCode,
        },
      ],
    };
  }

  public updateUserSocialTokenDetails(user: Data, originalData: Data) {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          return {
            modules: [
              {
                module_name: "update_user_social_token_details",
                social_id: user?.social_id,
                access_token: originalData?.credential?.accessToken,
                name: user?.socialUser?.displayName,
                fname: originalData?.additionalUserInfo?.profile?.given_name ?? originalData?.additionalUserInfo?.profile?.first_name,
                lname: originalData?.additionalUserInfo?.profile?.family_name ?? originalData?.additionalUserInfo?.profile?.last_name,
                home_town: "",
                location: "",
                dob: "",
                gender: "",
                email: user?.email,
                profile_image: user?.socialUser?.photoURL,
                language: user?.language_code,
                connect_type: user?.socialUser?.providerId,
                user_id: userId || originalData?.user?.uid,
              },
            ],
          };
        }));
    }
  }

  // This function is used to get the request body for get user communication preferences.  [this function is not call anywhere so comment it ]
  getUserPreferencesRequest() {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          return {
            modules: [
              {
                module_name: "get_customer_promotional_prefs",
                customer_id: userId,
                website_id: "33",
              },
            ],
            collection: `/users/${userId}/account_module`,
          };
        }));
    }
  }
  // This function is used to get the request body for update user communication preferences.
  getUpdateUserPreferencesRequest(sms_pref, email_pref) {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          return {
            modules: [
              {
                module_name: "update_customer_promotional_prefs",
                customer_id: userId,
                sms_pref,
                email_pref,
                push_pref: authService?.push_pref?.() ?? true,
                website_id: "33",
              },
            ],
          };
        }));
    }
  }
  // This function is used to get the request body for insert user's GDPR details
  getInsertUserGdpr(requestOption: string, ModuleName: string) {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of(null);
          }
          return of({
            modules: [
              {
                module_name: ModuleName,
                user_id: userId,
                gdpr_request: requestOption,
                requestid: uuidv4(),
                document: "/api_post_request/insert_user_gdpr",
              },
            ],
          });
        })
      );
    }
    return of(null);
  }


  // This function is used to get the request body for update MSN request
getUpdateMSNRequest(msn: string) {
  if (!this.validateRequest()) return of(null);
  const authService = this.injector.get(AuthService);
  return authService.getUid$("RequestUserService").pipe(
    map((userId) => {
      if (!userId) return null;
      return {
        modules: [
          {
            module_name: "update_customer_msn",
            user_id: userId,
            msn: msn,
            document: "/api_post_request/update_customer_msn",
            requestid: uuidv4(),
          },
        ],
      };
    })
  );
}

  // This function is used to get the request body for get about page contents. [this function is not call anywhere so comment it ]
  getAbout() {
    return {
      modules: [
        {
          module_name: "get_wp_page_contents",
          page_name: "about",
          user_id: "DFCI-02000000F468835B1515ABCD96DE414DE5E96CD19999EDB5F3B85F6953DD5CDFA4BF6605",
        },
      ],
    };
  }
  // This function is used to get the request body for get terms and conditions page contents for sign up page. [this function is not call anywhere so comment it ]
  getSignUpTermsAndConditions(contentType: string) {
    return {
      modules: [
        {
          page_id: 1,
          screen_id: 1,
          module_name: "get_wp_page_contents",
          page_name: contentType,
        },
      ],
    };
  }


  // This function is used to get the request body for get customer details for the user. [this function is not call anywhere so comment it ]
  getCustomerDetails() {
    const authService = this.injector.get(AuthService);
    return authService.getUid$("RequestUserService").pipe(
      switchMap(async (userId) => {
        if (!userId) {
          return of(null);
        }
        return {
          modules: [
            {
              page_id: 1,
              screen_id: 1,
              module_name: "get_customer_details",
              customer_id: userId,
            },
          ],
          collection: `/users/${userId}/account_module`,
        };
      }));
  }

  // This function is used to get the request body for redeem voucher code
  getRedeemUserVoucherCodeRequest(
    voucherCode: string,
    mobileNumber: string,
    prosub = "",
    document: any,
    syndicatePromotion: string,
    additionalAwardId: any
  ) {
    if (this.validateRequest()) {
      return this.commonService.getUid("redeemVoucher").pipe(
        map((userId) => {
          console.log("Fetched userId from CommonService:", userId);
          const module = {
                module_name: "validate_voucher",
                user_id: userId || "",
                voucher: voucherCode,
                email_address: "",
                mobile_number: mobileNumber,
                from_process: "mobile_app",
                prosub_id: prosub,
                requestid: uuidv4(),
                document: document,
                tp1: "tp1",
                tp2: "tp2",
                other: "Other",
                anonymous_id: localStorage.getItem("anonymous_id"),
                call_from: syndicatePromotion || "",
                additional_award_id: additionalAwardId || "",
              };
          return {
            modules: [
              this.salesJourneyIdentityService.applyIdentityEnvelope(module, {
                userId: userId || null,
                msn: mobileNumber,
                anonymousId: localStorage.getItem("anonymous_id"),
              }),
            ],
          };
        })
      );
    }

    // If validation fails, return an observable with an empty object
    console.warn("Validation failed for voucher request");
    return of({});
  }


  private async getUserIdFromLocalStorage(): Promise<string> {
    const authService = this.injector.get(AuthService);
    return await firstValueFrom(
      interval(1000).pipe(
        switchMap(() => authService.getUid$("RequestUserService")),
        filter((userId): userId is string => !!userId),
        take(1),
        timeout(50000),
        catchError(() => of(""))
      )
    );
  }

  // This function is used to get the request body for get voucher claim request [this function is not call anywhere so comment it ]
  getClaimUserVoucherRequest(claimVoucherDetails) {
    if (this.validateRequest()) {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            return of(null);
          }
          return {
            request: [
              {
                session_ID: "CommonService.session.session_id",
                page_ID: "20",
                screen_id: "20.2",
                action: "save_gift_voucher",
                website: "Lotto Social",
                website_id: "33",
                source_site: "mobi.lottosocial.com",
                module_name: "save_gift_voucher",
                customer_id: userId,
                voucher: claimVoucherDetails.voucherCode,
                mobile_number: claimVoucherDetails.mobileNumber,
                gift_status: claimVoucherDetails.giftStatus,
                visitor_id: claimVoucherDetails.visitorId,
                offer_id: claimVoucherDetails.offerId,
                prosub_id: "",
                tp1: "tp1",
                tp2: "tp2",
                tp3: "tp3",
                t_id: "t_id",
                other: "Other",
              },
            ],
          };
        }));
    }
  }
  // This function is used to get the request body for to maintain login logs
  public loginLogRequest(selectedLanguage: string) {
    const authService = this.injector.get(AuthService);
    return authService.getUid$("RequestUserService").pipe(
      switchMap(async (userId) => {
        if (!userId) {
          return of(null);
        }
        return {
          modules: [
            {
              module_name: "login_log",
              user_id: userId,
              param: {
                visitor_id: "123",
                website_id: "33",
                session_id: "CommonService.session.session_id",
                language_code: selectedLanguage ? selectedLanguage : "en",
              },
            },
          ],
        };
      }));
  }


  // This function is used to get the request body for reset password
  public resetPassword(newPassword: string): Observable<any> {
    return defer(() => {
      const authService = this.injector.get(AuthService);
      return authService.getUid$("RequestUserService").pipe(
        switchMap((userId) => {
          if (userId) {
            return of({
              modules: [
                {
                  module_name: "reset_password",
                  user_id: userId,
                  new_password: newPassword,
                },
              ],
            });
          } else {
            return throwError(() => new Error("User ID not found"));
          }
        }));
    }).pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((err, index) => {
            if (index < 2) {
              return timer(5000); // Retry after 5s
            } else {
              return throwError(() => err); // Give up after 3 attempts
            }
          })
        )
      )
    );
  }

  // This function is used to get the request body for update user password
  public updatePassword(password) {
    const authService = this.injector.get(AuthService);
    return authService.getUid$("RequestUserService").pipe(
      map((userId) => {
        if (!userId) {
          return null;
        }
        return {
          modules: [
            {
              module_name: "update_customer_password",
              user_id: userId,
              current_password: password.currentPassword,
              new_password: password.newPassword,
              requestid: uuidv4(),
              document: "/api_post_request/update_customer_password",
            },
          ],
        };
      }),
      filter((body) => body !== null) // <-- prevent null requests
    );
  }


  // This function is used to get the request body for update customer msn
  updateCustomeMsn(msn) {
    const authService = this.injector.get(AuthService);
    return authService.getUid$("RequestUserService").pipe(
      switchMap(async (userId) => {
        if (!userId) {
          return of(null);
        }
        return {
          modules: [
            {
              module_name: "update_customer_msn",
              user_id: userId,
              msn: msn,
            },
          ],
        };
      }));
  }


  //userpreferredlanguage
  // This function is used to get the request body for update user preferred language
  updateUserpreferredLanguage() {
    const authService = this.injector.get(AuthService);

    return authService.getUid$("RequestUserService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }

        return of({
          modules: [
            {
              module_name: "update_user_preferred_language",
              user_id: userId,
              language_code: localStorage.getItem("language_code") ?? "en",
            },
          ],
        });
      }));
  }


  // This method has been use to get user preferred currency   [this function is not call anywhere so comment it ]
  getUserPreferredCurrency() {
    const authService = this.injector.get(AuthService);
    authService.userDetails$.pipe(takeUntil(this.destroy$)).subscribe((user: Data) => {
      try {
        this.preferredCurrency = user?.preferred_currency ?? "GBP";
      } catch (error) {
        throw new Error(`Preferred Currency Not found ${error}`);
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public signInRequestForUsers(request: Data) {
    if (this.validateRequest()) {
      return {
        modules: [
          {
            module_name: "partial_registration_new_user",
            spoof_msn: true,
            social_id: request?.social_id,
            connect_type: request?.connect_type,
            country_code: request?.country_code ?? "44",
            email: request?.email,
          },
        ],
      };
    }
  }

  async getSocialPasswordSecretKey() {
    this.firestoreDataService.getV2CloudFunction("readfirestoredoc", {
      path_id: "DsHjjb5zZPACtZES8SIB",
      method: "read",
    })
    .pipe(
      map((response: any) => response?.data?.social_password_key),
      shareReplay({ bufferSize: 1, refCount: true })
    )
    .subscribe((secretKey: string) => {
      this.socialPassword.set(secretKey);
    });
  }
}
